import {brainpowerClient} from "./Repositories";
import {DataPoint, DataPointsResponse} from "@/models/response/BrainpowerApiResponse";
import {DataPointsApiRequestParams} from "@/models/DataPointsApiRequestParams";
import {ChartData, DataItem, DataSet} from "@/models/general";
import {Filters} from "@/models/Filters";
import {DataPointToChartDataParams} from "@/models/DataPointToChartDataParams";
import {uuidv4} from "@/components/utils/utils";
import {
  ChartNames,
  CONSUMER_INTERESTS,
  CONTENT_PAIRING_SECTION,
  CREATIVE_THEMES,
  DataPointTypes,
  PLATFORM_ANALYSIS_SECTION
} from "@/data/sections";
import * as pattern from 'patternomaly'
import {AVERAGE_COLOR, CHART_COLORS} from "@/consts/consts";

const chartNameToChartDataMap = new Map<string, DataPointToChartDataParams>()

const platformLabels = [{key: 'Desktop', label: 'Desktop'}, {
  key: 'Tablet',
  label: 'Tablet'
},{key: 'Mobile', label: 'Mobile'}]

const platformLabelsa = [{key: 'Mobile', label: 'Mobile'}, {key: 'Desktop', label: 'Desktop'}]


function mapKeysToLabels(labels: string[]) {
  return labels.map(l => ({key: l, label: l}))
}

chartNameToChartDataMap.set(ChartNames.CONS_PLAT_DAY, {
  dataPointName: DataPointTypes.CONS_PLAT_DAY,
  dataSetLabels: platformLabels,
  datasetKey: "platform",
  labels: mapKeysToLabels(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]),
  labelKey: "dayOfWeek",
  valueKeys: ["clicks"],


})

chartNameToChartDataMap.set(ChartNames.CONS_PLAT_HOUR, {
  dataPointName: DataPointTypes.CONS_PLAT_HOUR,
  dataSetLabels: platformLabels,
  datasetKey: "platform",
  labels: [{"key": 0, "label": "12 am"}, {"key": 1, "label": "1 am"}, {"key": 2, "label": "2 am"}, {
    "key": 3,
    "label": "3 am"
  }, {"key": 4, "label": "4 am"}, {"key": 5, "label": "5 am"}, {"key": 6, "label": "6 am"}, {
    "key": 7,
    "label": "7 am"
  }, {"key": 8, "label": "8 am"}, {"key": 9, "label": "9 am"}, {"key": 10, "label": "10 am"}, {
    "key": 11,
    "label": "11 am"
  }, {"key": 12, "label": "12 pm"}, {"key": 13, "label": "1 pm"}, {"key": 14, "label": "2 pm"}, {
    "key": 15,
    "label": "3 pm"
  }, {"key": 16, "label": "4 pm"}, {"key": 17, "label": "5 pm"}, {"key": 18, "label": "6 pm"}, {
    "key": 19,
    "label": "7 pm"
  }, {"key": 20, "label": "8 pm"}, {"key": 21, "label": "9 pm"}, {"key": 22, "label": "10 pm"}, {
    "key": 23,
    "label": "11 pm"
  }],
  labelKey: "hourOfDay", // hourOfDay
  valueKeys: ["clicks"],
})

chartNameToChartDataMap.set(ChartNames.CTR_PLATFORM, {
  dataPointName: DataPointTypes.CTR_PLATFORM,
  dataSetLabels: [{key: 'Desktop', label: 'Desktop'}, {key: 'Mobile', label: 'Mobile'}, {
    key: 'Tablet',
    label: 'Tablet'
  }],
  datasetKey: "platform",
  valueKeys: ["ctr"],
})

chartNameToChartDataMap.set(ChartNames.IMPR_BY_PLATFORM, {
  dataPointName: DataPointTypes.IMPR_BY_PLATFORM,
  dataSetLabels: platformLabels,
  datasetKey: "platform",
  valueKeys: ["impression"],
  titles: ["Impressions"]
})

chartNameToChartDataMap.set(ChartNames.CONV_BY_PLATFORM, {
  dataPointName: DataPointTypes.CONV_BY_PLATFORM,
  dataSetLabels: platformLabels,
  datasetKey: "platform",
  valueKeys: ["conversion"],
  titles: ["Conversions"]
})

chartNameToChartDataMap.set(ChartNames.CTR_REF_CATEGORY, {
  dataPointName: DataPointTypes.CTR_REF_CATEGORY,
  datasetKey: "category",
  valueKeys: ["ctr"],
  dataTransform: {normalizeValuesBetweenZeroAndOne: true, maxRows: 10},
})

chartNameToChartDataMap.set(ChartNames.CTR_USER_INTEREST, {
  dataPointName: DataPointTypes.CTR_USER_INTEREST,
  datasetKey: "category",
  valueKeys: ["ctr"],
  dataTransform: {normalizeValuesBetweenZeroAndOne: true}
})

chartNameToChartDataMap.set(ChartNames.CTR_IMPR_SUB_CATEGORY, {
  dataPointName: DataPointTypes.CTR_IMPR_SUB_CATEGORY,
  datasetKey: "category",
  valueKeys: ["ctr", "impression"],
  dataTransform: {normalizeValuesBetweenZeroAndOne: true}
})

chartNameToChartDataMap.set(ChartNames.CONV_REF_CATEGORY, {
  dataPointName: DataPointTypes.CONV_REF_CATEGORY,
  datasetKey: "category",
  valueKeys: ["conversion"],
  dataTransform: {omitZero: true}
})

chartNameToChartDataMap.set(ChartNames.CLICKS_USER_INTEREST, {
  dataPointName: DataPointTypes.CLICKS_USER_INTEREST,
  datasetKey: "category",
  valueKeys: ["clicks"],
  dataTransform: {omitZero: true, threshold: 5, showAverage: true}
})

chartNameToChartDataMap.set(ChartNames.IMP_REF_CATEGORY, {
  dataPointName: DataPointTypes.IMP_REF_CATEGORY,
  datasetKey: "category",
  valueKeys: ["impression"],
})

chartNameToChartDataMap.set(ChartNames.CTR_IMPR_CHAR_COUNT, {
  dataPointName: DataPointTypes.CTR_IMPR_CHAR_COUNT,
  dataSetLabels: platformLabelsa,
  datasetKey: "platform",
  valueKeys: ["impression"],
  labels: mapKeysToLabels(["<20", "20-40", "40-60", "60-80", "80-100", "100-120", "120+"]),
  labelKey: "category",
  dataTransform: {normalizeValuesBetweenZeroAndOne: true}
})

chartNameToChartDataMap.set(ChartNames.CTR_IMPR_CHAR_COUNT_2, {
  dataPointName: DataPointTypes.CTR_IMPR_CHAR_COUNT,
  dataSetLabels: platformLabelsa,
  datasetKey: "platform",
  valueKeys: ["ctr"],
  labels: mapKeysToLabels(["<20", "20-40", "40-60", "60-80", "80-100", "100-120", "120+"]),
  labelKey: "category",
  dataTransform: {normalizeValuesBetweenZeroAndOne: true}
})

chartNameToChartDataMap.set(ChartNames.CTR_KEYWORD, {
  dataPointName: DataPointTypes.CTR_KEYWORD,
  datasetKey: "keyword",
  valueKeys: ["ctr"],
})


export async function getPopularCategoriesResults(filters: Filters): Promise<DataItem[]> {
  const section = await getSection(CONTENT_PAIRING_SECTION, filters)
  //currently disable due to unclear behaviour
  // if (!ENVIRONMENT.isStaging && section.find(i => i.chartName === DataPointTypes.CONV_REF_CATEGORY)) {
  //   return section.filter(i => i.chartName !== DataPointTypes.IMP_REF_CATEGORY)
  // }
  return section
}


export async function getPerformanceByDevice(filters: Filters): Promise<DataItem[]> {
  return await getSection(PLATFORM_ANALYSIS_SECTION, filters)
}

export async function getConsumerInterestsResults(filters: Filters): Promise<DataItem[]> {
  const section = await getSection(CONSUMER_INTERESTS, filters)

  //match labels to same colors acc ross all charts in the section
  const labelsArr = section.map(s => s.data[0].labels)
  let labels =  [].concat(...labelsArr)
  labels =  [...new Set(labels)]
  const colorDictionary:any = {}
  labels.forEach((l: string, index:number) => {
    const colorIndex = index % CHART_COLORS.length
    colorDictionary[l] = l === 'Average' ?  AVERAGE_COLOR : CHART_COLORS[colorIndex]

  })
  section.forEach(s =>{

    s.data.forEach(d=>{
      const colors = d.labels.map(l => colorDictionary[l])
      const prevColors = colors.map(c => pattern.draw('diagonal-right-left',c))
      d.datasets.forEach((set, index) =>{
        set.backgroundColor = colors
        if(index){ //non-first set is prev data, color with pattern
          set.backgroundColor = prevColors
        }
      })
    })

  })

  return section
}

export async function getAdContentResults(filters: Filters): Promise<DataItem[]> {
  const section = await getSection(CREATIVE_THEMES, filters)

  section.forEach((dataItem : DataItem) => {
    if(dataItem.chartName.includes('CTR_IMPR_SUB_CATEGORY')){
      const supplyData =  dataItem.data[1] && dataItem.data[1].datasets[0].data;
      const demandData =  dataItem.data[0] && dataItem.data[0].datasets[0].data;

      const datasets:DataSet[] = [
        { backgroundColor: "#151E41",
          label: 'CTR',
          data: demandData,
          yAxisID: 'B',
          type: dataItem.chartType[1],
          pointRadius: 7,
          pointHoverRadius: 10
        },
        {
          label: 'Impressions',
          data: supplyData,
          backgroundColor: "#EE6519",
          yAxisID: 'A'}
      ]


      if(filters.includeWoWValues){
        const supplyDataPrev =  dataItem.data[1] && dataItem.data[1].datasets[1];
        const demandDataPrev = dataItem.data[0] && dataItem.data[0].datasets[1];

        supplyDataPrev.label = "Impressions (previous period)"
        demandDataPrev.label = "CTR (previous period)"
        demandDataPrev.type = dataItem.chartType[1]
        demandDataPrev.pointRadius = 7
        demandDataPrev.pointHoverRadius = 10
        supplyDataPrev.backgroundColor = new Array(demandDataPrev.data.length)
        supplyDataPrev.backgroundColor.fill(pattern.draw('diagonal-right-left','#EE6519'))
        demandDataPrev.backgroundColor = new Array(supplyDataPrev.data.length)
        demandDataPrev.backgroundColor.fill(pattern.draw('diagonal-right-left','#2f4392'))
        datasets.push(supplyDataPrev);
        datasets.splice(1,0,demandDataPrev)
      }

      dataItem.data.pop()
      dataItem.data[0].datasets = datasets
    }
    if(dataItem.chartName.includes('CTR_IMPR_CHAR_COUNT')){
      const supplyData=  dataItem.data[0] && dataItem.data[0].datasets[0].data;
      const demandData = dataItem.data[0] && dataItem.data[0].datasets[1].data;

      const datasets: DataSet[] = [
        {
          backgroundColor: "#151E41",
          label: 'Desktop',
          data: demandData,
        },
        {
          label: 'Mobile',
          data: supplyData,
          backgroundColor: "#EE6519",}
      ]
      if(filters.includeWoWValues){
        const demandDataPrev = dataItem.data[0] && dataItem.data[0].datasets[3];
        demandDataPrev.backgroundColor = new Array(demandDataPrev.data.length)
        demandDataPrev.backgroundColor.fill(pattern.draw('diagonal-right-left','#151E41'))
        const supplyDataPrev=  dataItem.data[0] && dataItem.data[0].datasets[2];
        supplyDataPrev.backgroundColor = new Array(supplyDataPrev.data.length)
        supplyDataPrev.backgroundColor.fill(pattern.draw('diagonal-right-left','#EE6519'))


        datasets.push(supplyDataPrev);
        datasets.splice(1,0,demandDataPrev)
      }
      dataItem.data[0].datasets = datasets
    }
  })

  return section
}

function normalizeValuesBetweenZeroAndOne(data: ChartData[]) {
  data.forEach(d => d.datasets.forEach(set => set.data = set.data.map((num: number) => num < 0 ? num : Math.max(1, num))))
}
function trimRows(data: ChartData[], max: number){
  data.forEach(d => {
    d.labels = d.labels.slice(0,max)
    d.datasets.forEach(set => set.data = set.data.slice(0, max))
  })
}

async function getSection(dataItems: DataItem[], filters: Filters): Promise<DataItem[]> {
  try {

    let section = dataItems.slice(0)

    const results = await Promise.all(section.map(p => getDataPoints(p.chartName, chartNameToChartDataMap.get(p.chartName).dataPointName, filters)))
    // console.log(results)
    for(let i=0; i < results.length; i++){
      const result = results[i]
        const item: DataItem = section[i]
        if (!result || !result.dataPoints[0]) {
          console.warn('no result', result)
          item.data = [];
        } else {
          const dataPoints = result.dataPoints
          item.data = getChartData(dataPoints, result.chartName, filters)
          item.periodEnd = new Date(dataPoints[0].periodEnd)
          item.periodStart = new Date(dataPoints[0].periodStart)
          item.id = result.dataPoints[0].datapointId + '-' + uuidv4()
          item.qcStatus = dataPoints.map(r => r.qcStatus).join(' ')
          item.woWValuesIncluded = dataPoints[0].woWValuesIncluded;
          item.prevData = []
          // retrieve previous data of charts that can't be displayed as assemble of charts
          if(!isPrevSupported(result.chartName) && filters.includeWoWValues){
            let prevPeriod = filters.prevPeriod;
            let prevFilter:Filters = {
              category:filters.category,
              region:filters.region,
              period:{code:prevPeriod},
              qcStatus:"('Passed','Suppressed')",
              includeWoWValues:0,

            }
            //console.log({name: result.chartName, prevFilter})
         const prevResult = await  getDataPoints( result.chartName,chartNameToChartDataMap.get(result.chartName).dataPointName,prevFilter)
         const prevDataPoints = prevResult.dataPoints
         item.prevData = getChartData(prevDataPoints, result.chartName, prevFilter);
          }
        }
      }

    section = section.filter(chartItem => {
      const hasData = !!chartItem?.data?.length
      const needPrevData = !!filters.includeWoWValues
      const prevDataValid = !!(chartItem.prevData?.length || chartItem.woWValuesIncluded)
      // console.log({name: chartItem.chartName, hasData, prevDataValid})
      if (hasData && !needPrevData || prevDataValid) {
          return chartItem
        } else {
          console.warn('item in section has no data/wow data', {section, item: chartItem})
          return false
        }
      }
    )
    return section

  } catch (err) {
    console.error("ERROR FETCHING SECTION", err);
    return []
  }
}

async function getDataPoints(chartName: string, datapointType: string, filters: Filters): Promise<DataPointsResponse> {
  try {

    const regionCode = filters.region ? filters.region.code : 'GG'// "Global"
    const categoryCode = filters.category ? filters.category.code : 'ALL' // "ALL"

    var params: DataPointsApiRequestParams = {
      datapointType: datapointType,
      verticalCode: categoryCode.toString(),
      regionCode: regionCode.toString(),
      periodStart: filters.period && filters.period.code.toString(),
      includeWoWValues: isPrevSupported(datapointType) ? filters.includeWoWValues : 0,
      qcStatus: filters.qcStatus || null
    }


    //there have been issues in non-dev environment with content type being set to application/x-www-form-urlencoded, and issues with 'Content-Type' header disrupting coors in local dev mode. hence the 'headers' code below
    const res = await brainpowerClient.post('datapoint/listDatapointBySearchCriteria', JSON.stringify(params), {
      headers: {
        'Content-Type': 'application/json',
      }
    })
    if (!res.data || !res.data.body) {
      console.warn('getDataPoints', '!res.data || !res.data.body', res)
      return {chartName: '', dataPoints: []}
    }
    const dataPoints = res.data.body as DataPoint[]

    dataPoints.forEach(d => d.datapointLines.sort((a, b) => {
      return a.dataPointLineId - b.dataPointLineId
    }))

    const response: DataPointsResponse = {
      chartName: chartName,
      dataPoints
    }

    return response;

  } catch (err) {
    console.error(err, datapointType, filters)
  }
}

function getChartData(dataPoints: DataPoint[], chartName: string, filters: Filters): ChartData[] {
  try {
    const params: DataPointToChartDataParams = chartNameToChartDataMap.get(chartName)
    const activateComparison:boolean = !!filters.includeWoWValues
    if (!dataPoints || !dataPoints.length) {
      console.error("no data points", {dataPoints, filters, params})
      return null;
    }

    if (!dataPoints.length) {
      return null;
    }
    const parsingFunction = !params.dataSetLabels ? dataPointToChartDataNoDatasetLabels : params.labels ? dataPointToChartData : dataPointToChartDataNoLabels
    const data = params.valueKeys.map((key, index) => (parsingFunction(dataPoints[0], params, key, params.titles ? params.titles[index] : '',activateComparison)))

    if (data && data.length && !data.find(item => !item.datasets.length || !!item.datasets.find(set => !set.data.length))) {


      if(params.dataTransform) {
        if (params.dataTransform.normalizeValuesBetweenZeroAndOne) {
          normalizeValuesBetweenZeroAndOne(data)
        }
        if (params.dataTransform.maxRows) {
          trimRows(data, params.dataTransform.maxRows)
        }
      }
      return data
    } else {
      console.warn('invalid data')
      return null
    }

  } catch (err) {
    console.error("getDataItem failed", {err, filters, chartName})
    return null
  }
}


//TODO:
// change 'datapointId' to 'id'

function dataPointToChartData(dataPoint: DataPoint, params: DataPointToChartDataParams, valueKey: string, title: string,
                              activateComparison: boolean): ChartData {
  try {

    if (!dataPoint || !dataPoint.datapointLines) {
      console.warn('no datapoint or datapoint lines received', dataPoint)
      return null
    }

    const chartData: ChartData = {
      title: title,
      datapointId: uuidv4() + '-' + dataPoint.datapointId,
      labels: params.labels.map(l => l.label),
      datasets: params.dataSetLabels.map(l => ({label: l.label, data: []})),
      insight: dataPoint.insightText,

    }

    let datasetPrevMap: any = new Map<string, { data: Array<any>, label: string,borderDash:[],backgroundColor: [] }>()
    params.dataSetLabels.forEach(value => {
      datasetPrevMap.set(value.label, {data: [], label:`${value.label} (previous period)`,borderDash:[10,5]});
    })

    dataPoint.datapointLines.forEach(line => {
      const dataset = chartData.datasets.find(d => d.label.toLowerCase() === line.key[params.datasetKey].toLowerCase());
      if (!dataset) {
        console.error('could not find datasetKey in line.key', {key: line.key, datasetKey: params.datasetKey})
      } else {
        const labelKeys = params.labels.map(l => l.key)
        const dataIndex = labelKeys.indexOf(line.key[params.labelKey]);
        dataset.data[dataIndex] = Math.round(line.values[valueKey])
        if (activateComparison) {
          datasetPrevMap.get(dataset.label).data[dataIndex] = line.wowValues? Math.round((line.wowValues[0].values as any)[valueKey]) : 0;
        }
      }

    })
    if (activateComparison) {
      chartData.datasets.push(...datasetPrevMap.values())
    }
    return chartData;
  } catch (err) {
    console.error(err)
    return null;
  }
}

function dataPointToChartDataNoLabels(dataPoint: DataPoint, params: DataPointToChartDataParams, valueKey: string, title: string,
                                      activateComparison: boolean): ChartData {
  if (!dataPoint || !dataPoint.datapointLines) {
    console.warn('no datapoint or datapoint lines received', dataPoint)
    return
  }

  try {
    const chartData: ChartData = {
      title: title,
      datapointId: uuidv4(),
      labels: params.dataSetLabels.map(l => l.label),
      datasets: [{data: []}],
      insight: dataPoint.insightText
    }

    const keys = params.dataSetLabels.map(l => l.key)
    const dataset = chartData.datasets[0]
   /*TODO: refactor. the colors and patters should not be set in this function. this function deals with data extraction not ui matters
     the non prev datasets do not set ther colors here
     */
    const datasetPrev: any = {data: [],backgroundColor: [
        pattern.draw('diagonal-right-left', '#151E41'),
        pattern.draw('diagonal-right-left', '#6F2F67'),
        pattern.draw('diagonal-right-left', '#CA4060')]}

    dataPoint.datapointLines.forEach(line => {

      const dataIndex = keys.indexOf(line.key[params.datasetKey])
      dataset.data[dataIndex] = Math.round((line.values as any)[valueKey])
      if (activateComparison && line.wowValues) {

        datasetPrev.data[dataIndex] = Math.round((line.wowValues[0].values as any)[valueKey])
      }
    })
    if (activateComparison) {

      chartData.datasets.push(datasetPrev)
    }
    // const chartDataPair:ChartDataPair = {data:chartData,dataPrev:chartDataPrev} as ChartDataPair


    return chartData;

  } catch (e) {
    console.error(e)
    return null
  }
}

function dataPointToChartDataNoDatasetLabels(dataPoint: DataPoint, params: DataPointToChartDataParams, valueKey: string,
                                             title: string, activateComparison: boolean): ChartData {
  if (!dataPoint || !dataPoint.datapointLines) {
    console.warn('no datapoint or datapoint lines received', dataPoint)
    return
  }

  let sum = 0;
  let prevSum = 0;
  const chartData: ChartData = {
    title: title,
    datapointId: uuidv4(),
    labels: [],
    datasets: [{data: []}],
    insight: dataPoint.insightText
  }

  const threshold: Boolean = params.dataTransform && params.dataTransform.threshold !== undefined;
  const showAverage: Boolean = params.dataTransform && params.dataTransform.showAverage !== undefined;
  const dataset = chartData.datasets[0]
  const datasetPrev: any = {data: [],backgroundColor: []}
  //pattern.draw('diagonal-right-left', '#CA4060')


  dataPoint.datapointLines = dataPoint.datapointLines.filter(line => {
    const value = Math.round(((line.values as any)[valueKey]))
    return !(threshold && value < params.dataTransform.threshold)
        && !(params.dataTransform && params.dataTransform.omitZero && value === 0)
  })

  dataPoint.datapointLines.forEach((line,index) => {
    const value = Math.round(((line.values as any)[valueKey]))
    let prev;
    if (activateComparison && isPrevSupported(dataPoint.datapointType.code)) {
      prev = line.wowValues ? Math.round(((line.wowValues[0].values as any)[valueKey])) : null
      if(Number.isInteger(prev)){ //TODO: if it is not , and we omit, we might mess up the average calculation
      prevSum += prev;
      }
    }
    sum += value

    chartData.labels.push(line.key[params.datasetKey].split('_').join(' '))
    dataset.data.push(value)
    //TODO: find a better way to model this then by checking chart names
    if (activateComparison && isPrevSupported(dataPoint.datapointType.code)) {
      datasetPrev.data.push(prev)
      datasetPrev.backgroundColor.push( pattern.draw('diagonal-right-left',CHART_COLORS[index]))
    }

  })
  if (activateComparison && isPrevSupported(dataPoint.datapointType.code)) {
    chartData.datasets.push(datasetPrev)
  }

  if (showAverage) {
    chartData.labels.push('Average');
    const avarage = sum / dataPoint.datapointLines.length;
    const prevAvarage = prevSum / dataPoint.datapointLines.length;
    if(activateComparison){
      datasetPrev.data.push(prevAvarage);
      datasetPrev.backgroundColor.push( pattern.draw('diagonal-right-left',AVERAGE_COLOR))
    }
    dataset.data.push(avarage);
  }


  return chartData;
}

function isPrevSupported(dataPointName: string){
  return [DataPointTypes.CONV_REF_CATEGORY,DataPointTypes.IMP_REF_CATEGORY, DataPointTypes.CTR_KEYWORD].indexOf(dataPointName) == -1

}
